/**
 * MessageTurn — renders a single conversation turn (one user message
 * → assistant response with interleaved tool calls + citations +
 * action cards + resolved-card remnants + error display).
 *
 * Canonical implementation shared between the embed surfaces (the
 * morph-bar in every host app) and the agent app's full-page chat.
 * This file is the source of truth — `apps/agent/client/src/pages/chat.tsx`
 * imports from here. Do NOT fork; if you need a per-surface variation,
 * add it as a prop or a child slot.
 */

import type { ActionCardResolution } from '@ordo/agent-tools'
import { summarizeToolArgs } from '@ordo/agent-tools/tool-status'
import { ConfirmCard } from '@ordo/ui/action-card-confirm'
import { CitationChip } from '@ordo/ui/citation-chip'
import { ErrorDisplay, toUiError } from '@ordo/ui/error-display'
import { ToolCallChip } from '@ordo/ui/tool-call-chip'
import { Check, ChevronDown, ChevronRight, Copy, Sparkles } from 'lucide-react'
import { useState } from 'react'

import { openSigninPopup } from '../lib/auth-popup'
import { cn } from '../lib/cn'
import { completionSummaryFor } from '../lib/completion-summary'
import { renderMarkdown } from '../lib/markdown'
import type { ChatMessage, ChatToolCall } from '../types'
import { ActionCardRenderer, ResolvedActionCardRenderer } from './action-card-renderer'
import { interleaveContentAndTools } from './interleave'

export interface MessageTurnProps {
  message: ChatMessage
  /** True for the first turn in the conversation — suppresses the
   *  top divider so the conversation doesn't open with a hairline. */
  isFirst?: boolean
  onDecideApproval?: (toolUseId: string, decision: 'approve' | 'skip') => void | Promise<void>
  onResolveCard?: (cardId: string, resolution: ActionCardResolution) => void | Promise<void>
  /** Click an inline image attachment to open a lightbox. Optional —
   *  when omitted, image attachments render as inline links. */
  onOpenImage?: (url: string) => void
  /** Re-attempt sending an errored user message. Engine knows the
   *  original payload via its retry-context map. */
  onRetry?: (messageId: string) => void | Promise<void>
  /** Click handler for a suggested-reply chip on this assistant turn.
   *  Only set on the latest assistant turn (older suggestions are
   *  stale relative to the current conversation state). */
  onSendSuggestion?: (text: string) => void | Promise<void>
}

export function MessageTurn({
  message,
  isFirst,
  onDecideApproval,
  onResolveCard,
  onOpenImage,
  onRetry,
  onSendSuggestion,
}: MessageTurnProps) {
  const isUser = message.role === 'user'

  if (isUser) {
    return (
      <div className={cn('flex min-w-0 justify-end py-4', !isFirst && 'border-t border-border/40')}>
        <div className="flex min-w-0 max-w-[680px] flex-col items-end gap-1.5">
          {message.slashCommand ? (
            // Slash-command bubble — render as the chip pill the user
            // pressed, not the expansion sentence the model received.
            // Reads as "I ran /help", not "I asked 'what can you do?'"
            <div className="as-msg-slash-pill">
              <span className="font-mono">/{message.slashCommand.trigger}</span>
              <span className="opacity-70">— {message.slashCommand.label}</span>
            </div>
          ) : (
            <div data-message-role="user" className="as-msg-user-bubble">
              {message.content}
            </div>
          )}
          {message.attachments && message.attachments.length > 0 ? (
            <div className="flex max-w-full flex-wrap justify-end gap-1.5">
              {message.attachments.map((a) => {
                const isImage = a.contentType?.startsWith('image/')
                if (isImage && onOpenImage) {
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => onOpenImage(a.fileUrl)}
                      className="as-msg-attachment-thumb">
                      <img src={a.fileUrl} alt={a.fileName} className="h-20 w-20 object-cover" />
                    </button>
                  )
                }
                return (
                  <a
                    key={a.id}
                    href={a.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="as-msg-attachment-chip typo-meta">
                    <span className="max-w-[200px] truncate">{a.fileName}</span>
                  </a>
                )
              })}
            </div>
          ) : null}
          {message.errorMessage ? (
            message.errorCategory === 'auth' ? (
              <ErrorDisplay
                error={toUiError(message.errorMessage, {
                  title: "You're signed out of Agent",
                  hint: 'Sign in to keep chatting.',
                  source: 'agent-loop',
                })}
              />
            ) : (
              <ErrorDisplay
                error={toUiError(message.errorMessage, {
                  title: "Couldn't send your message",
                  hint: 'Check your connection and try again.',
                  source: 'agent-loop',
                  onRetry: onRetry ? () => onRetry(message.id) : undefined,
                })}
              />
            )
          ) : null}
          {message.errorCategory === 'auth' ? (
            <div className="mt-2">
              <SignInPopupButton onSignedIn={() => onRetry?.(message.id)} />
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div className={cn('min-w-0 py-5', !isFirst && 'border-t border-border/40')}>
      <div className="typo-meta mb-2 flex items-center gap-1.5 font-medium">
        <Sparkles className="as-msg-sparkle h-3 w-3" />
        <span>Agent</span>
      </div>

      {(() => {
        const calls = message.toolCalls ?? []
        const hasPositional = calls.length > 0 && calls.every((c) => typeof c.position === 'number')
        if (!hasPositional) {
          // Legacy / no-tools path: render full content, then tool
          // disclosure (if any) at the bottom.
          return (
            <>
              {message.content ? (
                <div className="as-msg-prose" dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }} />
              ) : message.status === 'streaming' ? (
                <span className="text-sm text-muted-foreground">…</span>
              ) : null}
              {calls.length > 0 ? <ToolCallsDisclosure toolCalls={calls} /> : null}
            </>
          )
        }
        // Positional path: split content at each tool call's position so
        // pre-tool and post-tool text render as distinct blocks with the
        // tool card inline between them.
        const blocks = interleaveContentAndTools(message.content, calls)
        return (
          <>
            {blocks.map((b, i) =>
              b.kind === 'text' ? (
                <div
                  key={`t-${i}`}
                  className="as-msg-prose"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(b.content) }}
                />
              ) : (
                <InlineToolBlock key={`x-${b.call.id}`} call={b.call} />
              ),
            )}
            {blocks.length === 0 && message.status === 'streaming' ? (
              <span className="text-sm text-muted-foreground">…</span>
            ) : null}
          </>
        )
      })()}

      {message.pendingApprovals && message.pendingApprovals.length > 0 ? (
        <div className="space-y-2">
          {message.pendingApprovals.length > 1 ? (
            <div className="as-msg-approve-all">
              <span className="text-muted-foreground">
                {message.pendingApprovals.length} actions need your approval
              </span>
              <button
                type="button"
                onClick={() => {
                  for (const p of message.pendingApprovals ?? []) {
                    void onDecideApproval?.(p.toolUseId, 'approve')
                  }
                }}
                className="as-msg-approve-all-btn">
                Approve all
              </button>
            </div>
          ) : null}
          {message.pendingApprovals.map((p) => (
            <ConfirmCard
              key={p.toolUseId}
              slug={p.slug}
              summary={summarizeToolArgs(p.args) ?? undefined}
              rationale={p.rationale}
              details={JSON.stringify(p.args, null, 2)}
              onApprove={() => void onDecideApproval?.(p.toolUseId, 'approve')}
              onSkip={() => void onDecideApproval?.(p.toolUseId, 'skip')}
            />
          ))}
        </div>
      ) : null}

      {message.resolvedCards && message.resolvedCards.length > 0 ? (
        <div className="space-y-2">
          {message.resolvedCards.map((rc) =>
            rc.card ? (
              <ResolvedActionCardRenderer
                key={rc.cardId}
                card={rc.card}
                resolved={{ cardId: rc.cardId, resolution: rc.resolution }}
              />
            ) : (
              <div key={rc.cardId} className="as-msg-resolved-pill">
                <Check className="h-3 w-3" aria-hidden />
                <span>Resolved</span>
              </div>
            ),
          )}
        </div>
      ) : null}

      {message.pendingCard ? (
        <ActionCardRenderer
          pending={message.pendingCard}
          onResolve={(cardId, resolution) => void onResolveCard?.(cardId, resolution)}
        />
      ) : null}

      {message.errorMessage ? (
        <div className="mt-3">
          <ErrorDisplay
            error={toUiError(message.errorMessage, {
              title: "I couldn't generate a reply",
              hint: 'Try again, or rephrase if it keeps happening.',
              source: 'agent-loop',
              onRetry: onRetry ? () => onRetry(message.id) : undefined,
            })}
          />
        </div>
      ) : null}

      {/* [PROD-86] Completion confirmation for tool-using turns — the user
          shouldn't have to ask "did you do it?". */}
      {(() => {
        const summary = completionSummaryFor(message)
        if (!summary) {
          return null
        }
        return (
          <div
            className={cn(
              'mt-3 inline-flex items-center gap-2 rounded-md border bg-card px-2.5 py-1 text-xs text-muted-foreground',
              summary.kind === 'success' ? 'border-emerald-500/30' : 'border-border',
            )}>
            {summary.kind === 'success' ? <Check className="h-3 w-3 text-emerald-600" aria-hidden /> : null}
            <span>{summary.label}</span>
            {summary.detail ? <span className="text-muted-foreground/70">· {summary.detail}</span> : null}
          </div>
        )
      })()}

      {message.suggestedReplies && message.suggestedReplies.length > 0 && onSendSuggestion ? (
        <div className="mt-3 flex flex-wrap gap-1.5" aria-label="Suggested follow-ups">
          {message.suggestedReplies.map((s, i) => (
            <button
              key={`${message.id}-suggest-${i}`}
              type="button"
              onClick={() => void onSendSuggestion(s)}
              className="typo-meta rounded-full border border-border bg-background px-2.5 py-1 text-foreground/80 hover:border-foreground/40 hover:bg-muted">
              {s}
            </button>
          ))}
        </div>
      ) : null}

      {/* Plan 25 — always-visible Copy button. Hidden during streaming
       *  (incomplete payload), on error turns (competes with the
       *  ErrorDisplay), and on tool-only turns (no body to copy). */}
      {message.status === 'complete' && message.content.length > 0 && !message.errorMessage ? (
        <div className="mt-2">
          <CopyMessageButton content={message.content} />
        </div>
      ) : null}
    </div>
  )
}

function SignInPopupButton({ onSignedIn }: { onSignedIn?: () => void }) {
  const [busy, setBusy] = useState(false)
  const [blocked, setBlocked] = useState(false)
  const launch = async () => {
    setBusy(true)
    setBlocked(false)
    const result = await openSigninPopup()
    setBusy(false)
    if (result.ok) {
      onSignedIn?.()
      return
    }
    if (result.reason === 'blocked') {
      setBlocked(true)
    }
  }
  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => void launch()}
        disabled={busy}
        className="typo-meta inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 font-medium hover:bg-muted disabled:opacity-60">
        {busy ? 'Waiting for sign-in…' : 'Sign in to Agent →'}
      </button>
      {blocked ? (
        <p className="typo-meta text-muted-foreground">
          Popup blocked — allow popups for this site, or open a new tab and sign in.
        </p>
      ) : null}
    </div>
  )
}

function CopyMessageButton({ content }: { content: string }) {
  const [copied, setCopied] = useState(false)
  const onCopy = () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      return
    }
    void navigator.clipboard.writeText(content).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <button
      type="button"
      onClick={onCopy}
      className="typo-meta inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
      aria-label="Copy this message to clipboard">
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      <span>{copied ? 'Copied' : 'Copy'}</span>
    </button>
  )
}

/**
 * Collapsible "what tools ran" panel rendered at the bottom of an
 * assistant turn that has tool calls without `position` info (legacy
 * / no-positional path). Auto-expands while any call is in flight.
 */
function ToolCallsDisclosure({ toolCalls }: { toolCalls: ChatToolCall[] }) {
  const inFlight = toolCalls.some((tc) => !tc.status)
  const sources = toolCalls.reduce((n, tc) => n + (tc.citations?.length ?? 0), 0)
  const [userToggled, setUserToggled] = useState<boolean | null>(null)
  const expanded = userToggled ?? inFlight

  const summary = inFlight
    ? toolCalls.length === 1
      ? `Calling ${toolCalls[0]!.slug}…`
      : `Calling ${toolCalls.length} tools…`
    : `Used ${toolCalls.length === 1 ? toolCalls[0]!.slug : `${toolCalls.length} tools`}${
        sources > 0 ? ` · ${sources} ${sources === 1 ? 'source' : 'sources'}` : ''
      }`

  const Chevron = expanded ? ChevronDown : ChevronRight

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setUserToggled(!expanded)}
        className="typo-meta flex w-full items-center gap-1.5 text-left transition-colors hover:text-foreground">
        <span className="truncate">{summary}</span>
        <Chevron className="h-3 w-3 shrink-0 opacity-60" aria-hidden />
      </button>
      {expanded ? (
        <div className="mt-2 space-y-2">
          {toolCalls.map((tc) => (
            <div key={tc.id} className="flex flex-col gap-1.5">
              <ToolCallChip slug={tc.slug} status={tc.status} />
              {tc.citations && tc.citations.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 pl-1">
                  {tc.citations.map((c, i) => (
                    <CitationChip key={`${tc.id}-${c.url}-${i}`} citation={c} />
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Inline tool card rendered between text blocks (positional path).
 * Shows the tool's status, a one-line "what got run" summary derived
 * from the args, plus any citations the tool produced. Visually
 * distinct from `ToolCallsDisclosure` — appears exactly where the
 * model fired the call so the assistant turn reads top-to-bottom:
 * text, what-it-did, more text.
 */
function InlineToolBlock({ call }: { call: ChatToolCall }) {
  const summary = summarizeToolArgs(call.args)
  return (
    <div className="my-3 flex flex-col gap-1.5">
      <ToolCallChip slug={call.slug} status={call.status} />
      {summary ? (
        <div className="typo-meta ml-1 max-w-full truncate" title={summary}>
          <span className="opacity-60">→ </span>
          <span className="font-mono">{summary}</span>
        </div>
      ) : null}
      {call.citations && call.citations.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 pl-1">
          {call.citations.map((c, i) => (
            <CitationChip key={`${call.id}-${c.url}-${i}`} citation={c} />
          ))}
        </div>
      ) : null}
    </div>
  )
}
